import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { LegionProvider, Text } from '@foss/legion-ui'
import { findItem, slugFromPath } from '../nav/registry'
import { usePath } from '../routing/router'
import { useThemeMode } from '../theme/useThemeMode'
import { SideNav } from './SideNav'
import { TopBar } from './TopBar'
import styles from './Shell.module.css'

export interface ShellProps {
  /** Renders the body for a slug. Supplied by App so Shell stays layout-only. */
  renderPage: (slug: string) => ReactNode
}

export function Shell({ renderPage }: ShellProps) {
  const path = usePath()
  const slug = slugFromPath(path)
  const entry = findItem(slug)
  const { mode, toggle } = useThemeMode()

  const [navOpen, setNavOpen] = useState(false)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})

  const closeNav = useCallback(() => setNavOpen(false), [])
  /*
   * Flips the group's *effective* state, which is what SideNav passes in. An
   * earlier version recomputed the default here as `prev[id] ?? false`, which
   * disagreed with SideNav's `?? holdsCurrent` — so the group containing the
   * current page rendered expanded, and clicking it set `true` again and it
   * never closed.
   */
  const toggleGroup = useCallback((id: string, expanded: boolean) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !expanded }))
  }, [])

  // Navigating should land the reader at the top of the new page, not at the
  // scroll offset of the previous one.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [path])

  useEffect(() => {
    document.title = entry
      ? `${entry.item.name} — legion-ui`
      : 'legion-ui documentation'
  }, [entry])

  return (
    <LegionProvider
      scope="root"
      theme={{ scheme: mode }}
      className={styles.root}
      data-testid="app-root"
    >
      <a href="#main" className={styles.skip}>
        Skip to content
      </a>
      <TopBar
        mode={mode}
        onToggleTheme={toggle}
        onOpenNav={() => setNavOpen((v) => !v)}
        navOpen={navOpen}
      />
      <div className={styles.body}>
        <SideNav
          currentSlug={slug}
          openGroups={openGroups}
          onToggleGroup={toggleGroup}
          open={navOpen}
          onClose={closeNav}
        />
        <main id="main" className={styles.main} data-testid="main">
          <div className={styles.page}>
            {entry && (
              <header className={styles.pageHead}>
                <Text variant="display" as="h1" data-testid="page-title">
                  {entry.item.name}
                </Text>
                <Text
                  tone="muted"
                  className={styles.pageSummary}
                  data-testid="page-summary"
                >
                  {entry.item.summary}
                </Text>
              </header>
            )}
            {renderPage(slug)}
          </div>
        </main>
      </div>
    </LegionProvider>
  )
}
